import type { ReactNode } from "react";
import AdminPagination from "./AdminPagination";

interface AdminDataTableProps {
  headers: ReactNode[];
  columnWidths?: string[];
  colSpan: number;
  loading: boolean;
  empty: boolean;
  emptyText: ReactNode;
  emptyHint?: ReactNode;
  children: ReactNode;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export default function AdminDataTable({
  headers,
  columnWidths,
  colSpan,
  loading,
  empty,
  emptyText,
  emptyHint,
  children,
  total,
  page,
  pageSize,
  onPageChange,
}: AdminDataTableProps) {
  if (!loading && empty) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl flex flex-col items-center justify-center py-20 text-center">
        <span className="w-10 h-10 flex items-center justify-center mb-3">
          <i className="ri-file-search-line text-3xl text-gray-300"></i>
        </span>
        <p className="text-sm font-medium text-gray-400">{emptyText}</p>
        {emptyHint && <p className="text-xs text-gray-300 mt-1">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <table className="w-full table-fixed font-sans text-sm">
          {columnWidths && (
            <colgroup>
              {columnWidths.map((width, i) => (
                <col key={i} style={{ width }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr className="bg-gray-50">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="px-6 py-16 text-center text-sm text-gray-400">
                  <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
                  <div>Loading...</div>
                </td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>
      <AdminPagination
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={onPageChange}
      />
    </>
  );
}
